import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["startInput", "endInput", "label", "panel", "calendars", "status", "trigger"]
  static values = {
    start: String,
    end: String
  }

  connect() {
    this.startDate = this.parseISO(this.startValue) || this.today()
    this.endDate = this.parseISO(this.endValue) || this.startDate
    this.viewDate = this.firstDayOfMonth(this.startDate)
    this.isSelectingEnd = false
    this.boundOutsideClick = this.handleOutsideClick.bind(this)
    this.boundKeydown = this.handleKeydown.bind(this)

    document.addEventListener("click", this.boundOutsideClick)
    document.addEventListener("keydown", this.boundKeydown)
    this.updateValues()
    this.render()
  }

  disconnect() {
    document.removeEventListener("click", this.boundOutsideClick)
    document.removeEventListener("keydown", this.boundKeydown)
  }

  toggle(event) {
    event.preventDefault()
    this.panelTarget.hidden ? this.open() : this.close()
  }

  open() {
    this.panelTarget.hidden = false
    this.triggerTarget.setAttribute("aria-expanded", "true")
    this.render()
  }

  close() {
    this.panelTarget.hidden = true
    this.triggerTarget.setAttribute("aria-expanded", "false")
  }

  previousMonth(event) {
    event.preventDefault()
    this.viewDate = this.addMonths(this.viewDate, -1)
    this.render()
  }

  nextMonth(event) {
    event.preventDefault()
    this.viewDate = this.addMonths(this.viewDate, 1)
    this.render()
  }

  selectQuickRange(event) {
    event.preventDefault()

    const start = this.parseISO(event.currentTarget.dataset.start)
    const end = this.parseISO(event.currentTarget.dataset.end)
    if (!start || !end) return

    this.startDate = start
    this.endDate = end
    this.viewDate = this.firstDayOfMonth(start)
    this.isSelectingEnd = false
    this.updateValues()
    this.render()
  }

  selectDate(event) {
    const button = event.target.closest("[data-date]")
    if (!button) return

    event.preventDefault()
    const selectedDate = this.parseISO(button.dataset.date)
    if (!selectedDate) return

    if (!this.isSelectingEnd) {
      this.startDate = selectedDate
      this.endDate = selectedDate
      this.isSelectingEnd = true
    } else {
      if (selectedDate < this.startDate) {
        this.endDate = this.startDate
        this.startDate = selectedDate
      } else {
        this.endDate = selectedDate
      }

      this.isSelectingEnd = false
    }

    this.updateValues()
    this.render()
  }

  handleOutsideClick(event) {
    if (this.panelTarget.hidden || this.element.contains(event.target)) return

    this.close()
  }

  handleKeydown(event) {
    if (event.key !== "Escape" || this.panelTarget.hidden) return

    this.close()
    this.triggerTarget.focus()
  }

  render() {
    this.calendarsTarget.innerHTML = [0, 1]
      .map((offset) => this.renderMonth(this.addMonths(this.viewDate, offset)))
      .join("")

    this.statusTarget.textContent = this.isSelectingEnd
      ? "Agora escolha a data final"
      : `${this.formatDisplay(this.startDate)} até ${this.formatDisplay(this.endDate)}`
  }

  renderMonth(monthDate) {
    const monthStart = this.firstDayOfMonth(monthDate)
    const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0)
    const blanks = Array.from({ length: monthStart.getDay() }, () => '<span class="date-range-picker__blank"></span>')
    const days = []

    for (let day = 1; day <= monthEnd.getDate(); day += 1) {
      const date = new Date(monthStart.getFullYear(), monthStart.getMonth(), day)
      const iso = this.toISO(date)
      const classes = ["date-range-picker__day"]

      if (this.sameDay(date, this.today())) classes.push("is-today")
      if (this.sameDay(date, this.startDate)) classes.push("is-start")
      if (this.sameDay(date, this.endDate)) classes.push("is-end")
      if (date > this.startDate && date < this.endDate) classes.push("is-in-range")

      days.push(`
        <button type="button" class="${classes.join(" ")}" data-date="${iso}" aria-label="${this.formatDisplay(date)}">
          ${day}
        </button>
      `)
    }

    return `
      <section class="date-range-picker__month">
        <h3>${this.monthLabel(monthStart)}</h3>
        <div class="date-range-picker__weekdays">
          <span>D</span><span>S</span><span>T</span><span>Q</span><span>Q</span><span>S</span><span>S</span>
        </div>
        <div class="date-range-picker__days">
          ${blanks.join("")}${days.join("")}
        </div>
      </section>
    `
  }

  updateValues() {
    const startISO = this.toISO(this.startDate)
    const endISO = this.toISO(this.endDate)

    this.startInputTarget.value = startISO
    this.endInputTarget.value = endISO
    this.labelTarget.textContent = `${this.formatDisplay(this.startDate)} até ${this.formatDisplay(this.endDate)}`
  }

  parseISO(value) {
    const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (!match) return null

    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    return Number.isNaN(date.getTime()) ? null : date
  }

  toISO(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")

    return `${year}-${month}-${day}`
  }

  formatDisplay(date) {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    }).format(date)
  }

  monthLabel(date) {
    return new Intl.DateTimeFormat("pt-BR", {
      month: "long",
      year: "numeric"
    }).format(date)
  }

  today() {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), now.getDate())
  }

  firstDayOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1)
  }

  addMonths(date, amount) {
    return new Date(date.getFullYear(), date.getMonth() + amount, 1)
  }

  sameDay(left, right) {
    return this.toISO(left) === this.toISO(right)
  }
}
